// Global JavaScript for Aleph Shopify theme

class AlephTheme {
  constructor() {
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.initializeComponents();
  }

  setupEventListeners() {
    document.addEventListener('DOMContentLoaded', () => {
      // Aleph theme initialized
    });

    // Mobile menu toggle
    const menuToggle = document.querySelector('.mobile-menu-toggle');
    const mobileMenu = document.querySelector('.mobile-menu');

    if (menuToggle && mobileMenu) {
      menuToggle.addEventListener('click', () => {
        mobileMenu.classList.toggle('active');
      });
    }

    // Cart drawer functionality
    this.initCartDrawer();

    // Product variant selection
    this.initProductVariants();

    // Search functionality
    this.initSearch();
  }

  initializeComponents() {
    // Initialize any component-specific functionality
    this.initSliders();
    this.initModals();
    this.initTooltips();
  }

  initCartDrawer() {
    const cartDrawer = document.querySelector('.cart-drawer');
    const cartTriggers = document.querySelectorAll('[data-cart-drawer-trigger]');
    const cartClose = document.querySelector('.cart-drawer__close');

    cartTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.openCartDrawer();
      });
    });

    if (cartClose) {
      cartClose.addEventListener('click', () => {
        this.closeCartDrawer();
      });
    }

    // Close cart drawer when clicking outside
    if (cartDrawer) {
      cartDrawer.addEventListener('click', (e) => {
        if (e.target === cartDrawer) {
          this.closeCartDrawer();
        }
      });
    }
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('.cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.add('active');
      document.body.classList.add('cart-drawer-open');
    }
  }

  closeCartDrawer() {
    const cartDrawer = document.querySelector('.cart-drawer');
    if (cartDrawer) {
      cartDrawer.classList.remove('active');
      document.body.classList.remove('cart-drawer-open');
    }
  }

  initProductVariants() {
    const variantSelectors = document.querySelectorAll('.product-variant-selector');

    variantSelectors.forEach(selector => {
      selector.addEventListener('change', (e) => {
        this.updateProductVariant(e.target);
      });
    });
  }

  updateProductVariant(selector) {
    const productForm = selector.closest('.product-form');
    const variantId = selector.value;

    if (productForm && variantId) {
      // Update product price, availability, and images
      this.fetchVariantData(variantId)
        .then(variant => {
          this.updateProductDisplay(variant, productForm);
        })
        .catch(error => {
          console.error('Error fetching variant data:', error);
        });
    }
  }

  async fetchVariantData(variantId) {
    const response = await fetch(`${window.shopUrl}/variants/${variantId}.js`);
    return response.json();
  }

  updateProductDisplay(variant, productForm) {
    // Update price
    const priceElement = productForm.querySelector('.product-price');
    if (priceElement && variant.price) {
      priceElement.textContent = this.formatPrice(variant.price);
    }

    // Update availability
    const addToCartButton = productForm.querySelector('.add-to-cart-button');
    if (addToCartButton) {
      if (variant.available) {
        addToCartButton.disabled = false;
        addToCartButton.textContent = window.variantStrings.addToCart;
      } else {
        addToCartButton.disabled = true;
        addToCartButton.textContent = window.variantStrings.soldOut;
      }
    }
  }

  formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price / 100);
  }

  initSearch() {
    const searchInput = document.querySelector('.search-input');
    const searchResults = document.querySelector('.search-results');

    if (searchInput && searchResults) {
      let searchTimeout;

      searchInput.addEventListener('input', (e) => {
        clearTimeout(searchTimeout);
        const query = e.target.value.trim();

        if (query.length > 2) {
          searchTimeout = setTimeout(() => {
            this.performSearch(query, searchResults);
          }, 300);
        } else {
          searchResults.innerHTML = '';
          searchResults.classList.remove('active');
        }
      });
    }
  }

  async performSearch(query, resultsContainer) {
    try {
      const response = await fetch(`${window.routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`);
      const data = await response.json();

      this.displaySearchResults(data.resources.results.products, resultsContainer);
    } catch (error) {
      console.error('Search error:', error);
    }
  }

  displaySearchResults(products, container) {
    if (products.length === 0) {
      container.innerHTML = '<div class="search-no-results">No products found</div>';
    } else {
      const resultsHTML = products.map(product => `
        <div class="search-result-item">
          <a href="${product.url}" class="search-result-link">
            <img src="${product.featured_image?.url}" alt="${product.title}" class="search-result-image">
            <div class="search-result-content">
              <h4 class="search-result-title">${product.title}</h4>
              <p class="search-result-price">${this.formatPrice(product.price)}</p>
            </div>
          </a>
        </div>
      `).join('');

      container.innerHTML = resultsHTML;
    }

    container.classList.add('active');
  }

  initSliders() {
    // Initialize any sliders/carousels
    const sliders = document.querySelectorAll('.slider');
    sliders.forEach(slider => {
      // Add slider functionality here
    });
  }

  initModals() {
    // Initialize modal functionality
    const modalTriggers = document.querySelectorAll('[data-modal-trigger]');
    modalTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const modalId = trigger.getAttribute('data-modal-trigger');
        this.openModal(modalId);
      });
    });
  }

  openModal(modalId) {
    const modal = document.querySelector(`#${modalId}`);
    if (modal) {
      modal.classList.add('active');
      document.body.classList.add('modal-open');
    }
  }

  closeModal(modalId) {
    const modal = document.querySelector(`#${modalId}`);
    if (modal) {
      modal.classList.remove('active');
      document.body.classList.remove('modal-open');
    }
  }

  initTooltips() {
    // Initialize tooltip functionality
    const tooltips = document.querySelectorAll('[data-tooltip]');
    tooltips.forEach(tooltip => {
      tooltip.addEventListener('mouseenter', (e) => {
        this.showTooltip(e.target);
      });

      tooltip.addEventListener('mouseleave', (e) => {
        this.hideTooltip(e.target);
      });
    });
  }

  showTooltip(element) {
    const tooltipText = element.getAttribute('data-tooltip');
    const tooltip = document.createElement('div');
    tooltip.className = 'tooltip';
    tooltip.textContent = tooltipText;

    document.body.appendChild(tooltip);

    const rect = element.getBoundingClientRect();
    tooltip.style.left = `${rect.left + (rect.width / 2) - (tooltip.offsetWidth / 2)}px`;
    tooltip.style.top = `${rect.top - tooltip.offsetHeight - 8}px`;

    element._tooltip = tooltip;
  }

  hideTooltip(element) {
    if (element._tooltip) {
      document.body.removeChild(element._tooltip);
      delete element._tooltip;
    }
  }

  // Utility methods
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
}

// Initialize the theme
const alephTheme = new AlephTheme();

// Export for use in other scripts
window.AlephTheme = AlephTheme;

/* ═══════════════════════════════════════════════════════════════════════════════
   PHOTOSWIPE v5 LIGHTBOX
   Replaces custom lightbox — same external API, adds pinch-zoom + swipe-down
   ═══════════════════════════════════════════════════════════════════════════════ */

let _pswpInstance = null;

async function openImageLightbox(imageUrl, allImages, startIndex) {
  if (!imageUrl && (!allImages || !allImages.length)) return;
  const images = (allImages && allImages.length) ? allImages : [imageUrl];
  const index = (typeof startIndex === 'number') ? startIndex : 0;

  // Dynamically import PhotoSwipe ESM (lazy — only loads on first use)
  const { default: PhotoSwipe } = await import(
    'https://cdn.jsdelivr.net/npm/photoswipe@5/dist/photoswipe.esm.js'
  );

  if (_pswpInstance) {
    _pswpInstance.destroy();
    _pswpInstance = null;
  }

  _pswpInstance = new PhotoSwipe({
    dataSource: images.map(src => ({
      src: src,
      width: 1600,
      height: 1600,
    })),
    index: index,
    bgOpacity: 0.95,
    spacing: 0.1,
    loop: false,
    pinchToClose: true,
    closeOnVerticalDrag: true,
    showHideOpacity: true,
    initialZoomLevel: 'fit',
    secondaryZoomLevel: 2,
    maxZoomLevel: 3,
    paddingFn: () => ({ top: 0, bottom: 0, left: 0, right: 0 }),
    pswpModule: PhotoSwipe,
  });

  _pswpInstance.on('close', () => {
    _pswpInstance = null;
  });

  _pswpInstance.init();
}

function closeImageLightbox() {
  if (_pswpInstance) _pswpInstance.close();
}

window.openImageLightbox = openImageLightbox;
window.closeImageLightbox = closeImageLightbox;
